import type { AxiosError } from 'axios';
import { authClient } from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DashboardMetrics {
  /** Variants currently sitting in the photo queue */
  pendingPhotos: number;
  /** Variants currently sitting in the cleaning queue */
  pendingCleaning: number;
  /** Payment batches with scheduledDate = today (unpaid) */
  paymentsScheduledToday: number;
  /** Total unpaid payment batches with no scheduled date yet */
  unscheduledPaymentsCount: number;
  /** Consignors who joined in the last 7 days */
  newConsignorsThisWeek: number;
}

export type RecentVariantStatus = 'photo_queue' | 'cleaning_queue' | 'active' | 'sold' | 'inactive';

export interface RecentInventoryItem {
  variantId: number;
  productName: string;
  sizeName: string;
  artId: string | null;
  imageUrl: string | null;
  consignorName: string;
  publicPrice: number | null;
  status: RecentVariantStatus;
}

export type ActivityType =
  | 'item_received'
  | 'sale_completed'
  | 'payment_processed'
  | 'new_consignor';

export interface ActivityEvent {
  id: number;
  type: ActivityType;
  description: string;
  /** ISO 8601 timestamp */
  createdAt: string;
}

export interface AdminDashboardData {
  metrics: DashboardMetrics;
  recentInventory: RecentInventoryItem[];
  recentActivity: ActivityEvent[];
}

// ─── Parsers ──────────────────────────────────────────────────────────────────

const VALID_STATUSES: RecentVariantStatus[] = [
  'photo_queue',
  'cleaning_queue',
  'active',
  'sold',
  'inactive',
];

const VALID_ACTIVITY_TYPES: ActivityType[] = [
  'item_received',
  'sale_completed',
  'payment_processed',
  'new_consignor',
];

function parseMetrics(raw: unknown): DashboardMetrics {
  const empty: DashboardMetrics = {
    pendingPhotos: 0,
    pendingCleaning: 0,
    paymentsScheduledToday: 0,
    unscheduledPaymentsCount: 0,
    newConsignorsThisWeek: 0,
  };
  if (!raw || typeof raw !== 'object') return empty;
  const r = raw as Record<string, unknown>;
  return {
    pendingPhotos: Number(r.pendingPhotos ?? r.pending_photos ?? 0),
    pendingCleaning: Number(r.pendingCleaning ?? r.pending_cleaning ?? 0),
    paymentsScheduledToday: Number(r.paymentsScheduledToday ?? r.payments_scheduled_today ?? 0),
    unscheduledPaymentsCount: Number(
      r.unscheduledPaymentsCount ?? r.unscheduled_payments_count ?? 0
    ),
    newConsignorsThisWeek: Number(r.newConsignorsThisWeek ?? r.new_consignors_this_week ?? 0),
  };
}

function parseRecentItem(raw: unknown): RecentInventoryItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const variantId = Number(r.variantId ?? r.variant_id);
  if (!Number.isFinite(variantId) || variantId <= 0) return null;
  const statusRaw = String(r.status ?? 'active');
  const imageUrlRaw = r.imageUrl ?? r.image_url ?? r.thumbnailUrl;
  const artIdRaw = r.artId ?? r.art_id;
  const priceRaw = r.publicPrice ?? r.public_price;
  return {
    variantId,
    productName: String(r.productName ?? r.product_name ?? '').trim(),
    sizeName: String(r.sizeName ?? r.size_name ?? '').trim(),
    artId: typeof artIdRaw === 'string' && artIdRaw.trim() ? artIdRaw.trim() : null,
    imageUrl: typeof imageUrlRaw === 'string' && imageUrlRaw.trim() ? imageUrlRaw.trim() : null,
    consignorName: String(r.consignorName ?? r.consignor_name ?? '').trim(),
    publicPrice: priceRaw != null && Number.isFinite(Number(priceRaw)) ? Number(priceRaw) : null,
    status: VALID_STATUSES.includes(statusRaw as RecentVariantStatus)
      ? (statusRaw as RecentVariantStatus)
      : 'active',
  };
}

function parseActivity(raw: unknown): ActivityEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const typeRaw = String(r.type ?? '');
  const createdAtRaw = r.createdAt ?? r.created_at;
  return {
    id,
    type: VALID_ACTIVITY_TYPES.includes(typeRaw as ActivityType)
      ? (typeRaw as ActivityType)
      : 'item_received',
    description: String(r.description ?? '').trim(),
    createdAt:
      typeof createdAtRaw === 'string' && createdAtRaw ? createdAtRaw : new Date(0).toISOString(),
  };
}

// ─── Exported function ────────────────────────────────────────────────────────

export type AdminDashboardResult =
  | { ok: true; data: AdminDashboardData }
  | { ok: false; error: string };

/**
 * GET /api/admin/dashboard
 *
 * Returns all data needed to render the admin home page: KPI metrics,
 * the 5 most recently added inventory variants, and the 10 most recent
 * activity events.
 *
 * Auth: admin.
 *
 * Response 200:
 * {
 *   "data": {
 *     "metrics": {
 *       "pendingPhotos": 8,           // variants in photo_queue status
 *       "pendingCleaning": 5,         // variants in cleaning_queue status
 *       "paymentsScheduledToday": 2,  // unpaid batches with scheduledDate = today
 *       "unscheduledPaymentsCount": 7,// unpaid batches with no scheduledDate
 *       "newConsignorsThisWeek": 3    // users who registered in the last 7 days
 *     },
 *     "recentInventory": [
 *       {
 *         "variantId": 1,
 *         "productName": "Jordan 1 High Chicago",
 *         "sizeName": "10.5 US",
 *         "artId": "42",
 *         "imageUrl": "https://...",
 *         "consignorName": "Carlos Mendoza",
 *         "publicPrice": 14500.00,
 *         "status": "photo_queue"  // photo_queue | cleaning_queue | active | sold | inactive
 *       }
 *     ],
 *     "recentActivity": [
 *       {
 *         "id": 1,
 *         "type": "new_consignor",  // item_received | sale_completed | payment_processed | new_consignor
 *         "description": "Sofia Garcia se unió como consignadora",
 *         "createdAt": "2024-10-14T10:00:00Z"
 *       }
 *     ]
 *   }
 * }
 *
 * Notes:
 *   - recentInventory is ordered by the variant's receivedAt DESC, limit 5.
 *   - recentActivity is ordered by createdAt DESC, limit 10.
 * Errors: 403, 500.
 */
export async function fetchAdminDashboard(): Promise<AdminDashboardResult> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/dashboard');
    const body = data as Record<string, unknown>;
    const inner = (typeof body.data === 'object' && body.data ? body.data : body) as Record<
      string,
      unknown
    >;

    const rawInventory = inner.recentInventory ?? inner.recent_inventory;
    const rawActivity = inner.recentActivity ?? inner.recent_activity;

    return {
      ok: true,
      data: {
        metrics: parseMetrics(inner.metrics),
        recentInventory: Array.isArray(rawInventory)
          ? rawInventory.map(parseRecentItem).filter((x): x is RecentInventoryItem => x !== null)
          : [],
        recentActivity: Array.isArray(rawActivity)
          ? rawActivity.map(parseActivity).filter((x): x is ActivityEvent => x !== null)
          : [],
      },
    };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo cargar el dashboard.',
    };
  }
}
